import { useState } from 'react';
import {
  captureSummaryV3,
  fmtCount,
  fmtMoneyCompact,
  governedSettings,
  governedStatus,
  governedTenantCreate,
  governedTenantPause,
  governedTenantPromote,
  governedTenants,
  metricsMonitoring,
  oversightChannels,
  oversightGuards,
  oversightHeld,
  pulseIncidents,
  tenantKeyRotate,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { Act, residentLine, useApi, useNow } from './common';
import { useBuildText, useCustomerText } from '../../ui/buildLabels';

// design monVals: chip(), tile(), fig()
const chip = (on: boolean) =>
  'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:6px;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;text-decoration:none;flex:0 0 auto;' +
  (on ? 'background:#2F5C97;color:#fff;' : 'background:transparent;color:#54606C;');
const tile = (i: number, n: number) => 'padding:13px 16px;box-sizing:border-box;' + (i < n - 1 ? 'border-right:1px solid rgba(34,39,46,.09);' : '');
const fig = (c: string) => 'font-size:26px;font-weight:700;letter-spacing:-.01em;line-height:1;color:' + c + ';';
const tag = (fg: string, bg: string) => 'font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.06em;border-radius:5px;padding:2px 7px;flex:0 0 auto;white-space:nowrap;color:' + fg + ';background:' + bg + ';';
const CH: Record<string, [string, string, string]> = {
  SCREENING: ['#1F6B52', 'rgba(47,138,111,.13)', '#1F6B52'],
  STAGED: ['#54606C', 'rgba(34,39,46,.08)', '#54606C'],
  DEGRADED: ['#5B6B7C', 'rgba(91,107,124,.14)', '#5B6B7C'],
  DOWN: ['#B3261E', 'rgba(179,38,30,.13)', '#B3261E'],
};
/** "4m", "2h", "2h 5m" (design ageOf). */
const ageMin = (sec: number) => {
  const m = Math.max(0, Math.floor(sec / 60));
  return m < 60 ? m + 'm' : Math.floor(m / 60) + 'h' + (m % 60 ? ' ' + (m % 60) + 'm' : '');
};
const LABEL = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';

/**
 * Governed Systems, the detail behind the Summary (design monIsGov, T14365): fleets and
 * channels, what is held, incidents, and each governed system with its usage. Reached
 * from /oversight?tab=systems.
 */
export function GovernedSystems({ onBack }: { onBack: () => void }) {
  const plain = useCustomerText();
  const { client } = useSession();
  const roReason = useReadOnlyReason();
  const now = useNow(1000);
  const [filter, setFilter] = useState<'ALL' | 'ENFORCING' | 'STAGED'>('ALL');
  const [selId, setSelId] = useState<string | null>(null);
  const [modal, setModal] = useState(false);
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState<'pause' | 'promote' | 'rotate' | null>(null);
  const [newKey, setNewKey] = useState<{ id: string; key: string } | null>(null);
  const { role } = useConsoleRole();
  const manager = role === 'SUPER_ADMIN' || role === 'OPERATOR';

  const tenants = useApi((c, signal) => governedTenants(c, { signal }), []);
  const status = useApi((c, signal) => governedStatus(c, { signal }), []);
  const mon = useApi((c, signal) => metricsMonitoring(c, { query: { windowHours: 24 }, signal }), []);
  const guards = useApi((c, signal) => oversightGuards(c, { signal }), []);
  const channels = useApi((c, signal) => oversightChannels(c, { signal }), []);
  const held = useApi((c, signal) => oversightHeld(c, { query: { limit: 20 }, signal }), []);
  // The capture engine's line under the usage (design ovAgingLine), from the network capture summary.
  const capture = useApi((c, signal) => captureSummaryV3(c, { signal }), []);
  const bt = useBuildText();
  const n = capture.data;
  const agingLine =
    n && n.engine && n.denialRate
      ? bt('Engine ' + (n.engine.live ? 'live' : 'not live') + ' · ' + fmtCount(n.engine.connectedFacilities) + ' ' + (n.engine.connectedFacilities === 1 ? 'facility' : 'facilities') +
        ' connected · dollars, windows and denials ' + (n.recoverable.basis === 'measured' ? 'MEASURED' : 'MODELED') + ' · ' + n.denialRate.pct + '% denial rate (' + n.denialRate.source + ') · ' +
        fmtMoneyCompact(n.recoverable.value == null ? null : (n.recoverable.value / 100) * (n.denialRate.pct / 100)) + ' denials · root cause and AR 90+ pending the RCM rollup')
      : '';
  const inc = useApi((c, signal) => pulseIncidents(c, { query: { status: 'open' }, signal }), []);
  const settings = useApi((c, signal) => governedSettings(c, { signal }), []);
  const failClosed = settings.data?.settings.gate?.failClosed;

  const SYS = (tenants.data?.tenants ?? []).map((t) => ({ ...t, live: t.lifecycle !== 'staged' }));
  const shown = SYS.filter((x) => filter === 'ALL' || (filter === 'ENFORCING') === x.live);
  const sel = SYS.find((x) => x.id === selId) ?? SYS[0] ?? null;
  const v = mon.data?.verdicts;
  const nTot = v?.total ?? null;
  const policiesOf = (id: string) => (status.data && status.data.tenant.id === id ? String(status.data.activePolicies) : '–');
  const gN = guards.data ? String(guards.data.guards.length) : '–';
  const liveN = SYS.filter((x) => x.live).length;
  const incidents = (inc.data?.data.incidents ?? []).filter((i) => i.status !== 'resolved');
  const heldItems = held.data?.items ?? [];
  const pctOf = (n: number) => (nTot ? ' (' + Math.round((n / nTot) * 100) + '%)' : '');

  const tiles = [
    { label: 'Atlas fleets', fig: tenants.data ? String(SYS.length) : '–', style: fig('#2F5C97'), note: liveN + ' live · ' + (SYS.length - liveN) + ' staged' },
    { label: 'Governed actions · 24H', fig: nTot === null ? '–' : String(nTot), style: fig('#2F5C97'), note: 'across ' + SYS.length + ' fleet' + (SYS.length === 1 ? '' : 's') },
    { label: 'Guards armed', fig: gN, style: fig('#2F5C97'), note: guards.data ? 'guard registry' : plain('not served yet') },
    { label: 'Policies', fig: status.data ? String(status.data.activePolicies) : '–', style: fig('#2F5C97'), note: 'live · sealed to ledger' },
    {
      label: 'Default posture',
      fig: failClosed === undefined ? '–' : failClosed ? 'DENY' : 'ALLOW',
      style: 'font-size:19px;font-weight:700;color:' + (failClosed === false ? '#B3261E' : failClosed ? '#1F6B52' : '#54606C') + ';',
      note: failClosed === undefined ? plain('not served yet') : failClosed ? 'all systems' : 'passes on error',
    },
  ];

  const create = async () => {
    const n = name.trim();
    if (!n) {
      setErr('Give the system a name before creating it.');
      return;
    }
    setBusy(true);
    try {
      const r = await governedTenantCreate(client, { body: { name: n, confirm: true } });
      setModal(false);
      setSelId(r.tenant.id);
      setFilter('ALL');
      tenants.reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Act onClick={onBack} css="flex:0 0 auto;display:inline-flex;align-items:center;min-height:32px;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;">
        ← Back to Monitoring
      </Act>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(286px,1fr));gap:12px;align-items:start;min-width:0;flex:0 0 auto;')}>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;flex:0 0 auto;')}>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-bottom:12px;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:#1F6B52;')} />
            <span style={sx('font-size:16px;font-weight:700;')}>Fleets &amp; channels</span>
            <span style={sx('flex:1;')} />
          </div>
          <div style={sx('font-size:16px;color:#54606C;margin-bottom:11px;')}>{channels.data?.summary ?? (channels.error ? plain('Fleet and channel health is not served yet.') : '–')}</div>
          <div style={sx('max-height:240px;overflow-y:auto;overflow-x:auto;min-width:0;padding-right:3px;')}>
            {(channels.data?.channels ?? []).map((s) => {
              const c = CH[s.state] ?? CH.STAGED;
              return (
                <div key={s.name} style={sx('display:flex;align-items:flex-start;gap:10px;padding:9px 0;border-top:1px solid rgba(34,39,46,.08);')}>
                  <span style={sx('width:7px;height:7px;border-radius:50%;margin-top:5px;flex:0 0 7px;background:' + c[2] + ';')} />
                  <div style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                    <div style={sx('font-size:16px;font-weight:600;')}>{s.name}</div>
                    <div style={sx('font-size:14px;color:#54606C;margin-top:2px;')}>{s.meta}</div>
                  </div>
                  <span style={sx(tag(c[0], c[1]))}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;')}>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:' + (heldItems.length ? '#5B6B7C' : '#1F6B52') + ';')} />
            <span style={sx('font-size:16px;font-weight:700;')}>Held · awaiting human</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:16px;color:#54606C;')}>
              <b style={sx('color:#22272E;')}>{held.data ? heldItems.length : '–'}</b> in queue
            </span>
          </div>
          {heldItems.map((h) => (
            <div key={h.id} style={sx('padding:9px 0;border-top:1px solid rgba(34,39,46,.08);')}>
              <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.06em;color:#5B6B7C;background:rgba(91,107,124,.14);border-radius:4px;padding:1px 6px;flex:0 1 auto;min-width:0;')}>HOLD</span>
                <span style={sx('font-size:16px;font-weight:600;min-width:0;overflow-wrap:break-word;')}>{[residentLine(h.resident) ?? 'AI action', h.reason ?? h.gate].join(' · ')}</span>
              </div>
              <div style={sx('font-size:14px;color:#54606C;margin-top:4px;')}>{ageMin((now - Date.parse(h.surfacedAt)) / 1000) + ' · → ' + (h.waitingOn ?? (h.routed ? 'with the nurse' : 'held for a person'))}</div>
            </div>
          ))}
          {held.data && heldItems.length === 0 && <div style={sx('font-size:16px;color:#54606C;text-align:center;padding:16px 0;')}>No actions held for review</div>}
        </div>

        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;')}>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:#B3261E;')} />
            <span style={sx('font-size:16px;font-weight:700;')}>Incidents &amp; Scams</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:16px;color:#54606C;')}>{inc.data ? incidents.length + ' open' : '–'}</span>
          </div>
          <div style={sx('display:flex;flex-direction:column;gap:9px;')}>
            <div style={sx('min-width:0;')}>
              {incidents.map((i) => {
                // critical in red, everything else (a degraded channel) in slate (design monIncidents)
                const red = i.severity === 'critical';
                return (
                  <div key={i.id} style={sx('border-radius:9px;padding:10px 12px;background:' + (red ? 'rgba(179,38,30,.07)' : 'rgba(91,107,124,.08)') + ';border:1px solid ' + (red ? 'rgba(179,38,30,.24)' : 'rgba(91,107,124,.26)') + ';')}>
                    <div style={sx('font-size:16px;font-weight:700;color:' + (red ? '#B3261E' : '#5B6B7C') + ';')}>{i.title}</div>
                    <div style={sx('font-size:14px;color:#54606C;margin-top:4px;line-height:1.5;')}>{typeof i.detail?.summary === 'string' ? (i.detail.summary as string) : i.signal}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-bottom:14px;')}>
        {(['ALL', 'ENFORCING', 'STAGED'] as const).map((n) => (
          <Act key={n} pressed={filter === n} css={chip(filter === n)} onClick={() => setFilter(n)}>
            {n}
          </Act>
        ))}
        <span style={sx('display:inline-flex;align-items:center;gap:7px;font-size:16px;color:#54606C;margin-left:5px;')}>
          <span style={sx('width:7px;height:7px;border-radius:50%;background:#1F6B52;')} />
          {liveN + ' live · ' + (SYS.length - liveN) + ' staged'}
        </span>
        <span style={sx('flex:1;')} />
        <Act
          disabled={!!roReason}
          title={roReason ?? undefined}
          onClick={() => {
            setModal(true);
            setName('');
            setErr(null);
          }}
          css="display:inline-flex;align-items:center;gap:7px;height:32px;padding:0 14px;border-radius:8px;background:#2F5C97;color:#fff;font-size:16px;font-weight:600;text-decoration:none;flex:0 1 auto;min-width:0;white-space:nowrap;"
        >
          Add System
        </Act>
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(152px,1fr));box-sizing:border-box;flex:0 0 auto;border-top:1px solid rgba(34,39,46,.14);margin-bottom:14px;min-width:0;')}>
        {tiles.map((t, i) => (
          <div key={t.label} style={sx(tile(i, tiles.length))}>
            <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:9px;')}>{t.label}</div>
            <div style={sx(t.style)}>{t.fig}</div>
            <div style={sx('font-size:16px;color:#54606C;margin-top:6px;')}>{t.note}</div>
          </div>
        ))}
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:14px;align-items:start;')}>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;min-width:0;')}>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:13px;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
            <span style={sx('font-size:16px;font-weight:700;')}>Connected Systems</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:16px;color:#54606C;')}>{SYS.length + (SYS.length === 1 ? ' system' : ' systems') + ' · gate-and-forward'}</span>
          </div>
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:13px;')}>
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(232px,1fr));gap:11px;min-width:0;')}>
              {shown.map((x) => (
                <Act
                  key={x.id}
                  pressed={sel?.id === x.id}
                  onClick={() => setSelId(x.id)}
                  css={'display:block;text-decoration:none;color:inherit;border-radius:11px;padding:14px 15px;background:#FBF8F1;border:1px solid ' + (sel?.id === x.id ? 'rgba(47,92,151,.4);' : 'rgba(34,39,46,.11);')}
                >
                  <div style={sx('display:flex;align-items:flex-start;gap:11px;')}>
                    <span style={sx('display:flex;align-items:center;justify-content:center;width:32px;height:32px;flex:0 0 32px;border-radius:8px;background:rgba(79,138,214,.13);color:#2F5C97;font-size:16px;')}>◆</span>
                    <div style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                      <div style={sx('font-size:16px;font-weight:700;')}>{x.name}</div>
                      <div style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;margin-top:3px;overflow-wrap:break-word;')}>{x.id}</div>
                    </div>
                    <span style={sx(tag(x.live ? '#1F6B52' : '#54606C', x.live ? 'rgba(47,138,111,.13)' : 'rgba(34,39,46,.08)'))}>{x.live ? 'ENFORCING' : 'STAGED'}</span>
                  </div>
                  <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:11px;margin-top:14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.08);')}>
                    {[
                      ['Policies', x.live ? policiesOf(x.id) : '0'],
                      ['Guards', x.live ? gN : '0'],
                      ['Gate checks', x.live && nTot !== null ? String(nTot) : '-'],
                      ['Throughput', x.live && v ? v.throughputPerMin.toFixed(2) + '/min' : '-'],
                    ].map(([k, val]) => (
                      <div key={k}>
                        <div style={sx(LABEL + 'margin-bottom:3px;')}>{k}</div>
                        <div style={sx('font-size:16px;font-weight:700;color:' + (x.live ? '#22272E' : '#54606C') + ';')}>{val}</div>
                      </div>
                    ))}
                  </div>
                </Act>
              ))}
            </div>
          </div>
        </div>
        {sel && (
          <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;min-width:0;')}>
            <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-bottom:4px;')}>
              <span style={sx('font-size:16px;font-weight:700;')}>{sel.name}</span>
              <span style={sx('flex:1;')} />
              {manager && (
                // Restored from the Flutter console (no design control): promote or pause the
                // gate, and rotate this system's key. The server allows the operator tier only.
                <>
                  <Act
                    disabled={!!roReason || busy}
                    title={roReason ?? undefined}
                    onClick={() => setAsk(sel.live ? 'pause' : 'promote')}
                    css="font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;"
                  >
                    {sel.live ? 'Pause' : 'Promote to live'}
                  </Act>
                  <Act
                    disabled={!!roReason || busy}
                    title={roReason ?? undefined}
                    onClick={() => setAsk('rotate')}
                    css="font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;"
                  >
                    Rotate key
                  </Act>
                </>
              )}
            </div>
            <div style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;margin-bottom:12px;word-break:break-all;')}>{sel.id}</div>
            <div style={sx('font-size:16px;color:#54606C;line-height:1.55;margin-bottom:13px;')}>
              {sel.live
                ? 'Governed fleet, gate-and-forward. Every action is screened before delivery and sealed to the Truth Ledger.'
                : 'Staged. The gate is wired but no traffic has been routed through it yet, so there is nothing to report until it is promoted to live.'}
            </div>
            <div style={sx(LABEL + 'margin-bottom:6px;')}>API key</div>
            <div style={sx('font-variant-numeric:tabular-nums;font-size:16px;color:#22272E;padding:8px 11px;margin-bottom:14px;word-break:break-all;')} data-testid="governed-key">
              {newKey && newKey.id === sel.id ? newKey.key : sel.apiKey ? '••••••••' + sel.apiKey.slice(-3) : 'Not shown'}
            </div>
            {newKey && newKey.id === sel.id && (
              <div role="status" style={sx('font-size:14px;color:#B3261E;line-height:1.45;margin:-8px 0 12px;')}>Save this key now: it is shown once, and the old key already fails.</div>
            )}
            {err && !modal && <div role="alert" style={sx('font-size:14px;color:#B3261E;margin:-6px 0 10px;')}>{err}</div>}
            <div style={sx(LABEL + 'margin-bottom:9px;')}>Usage · 24h</div>
            {(sel.live && v
              ? [
                  ['Gate checks', String(v.total), '#2F5C97'],
                  ['Gate latency · p50', mon.data?.latency.p50 !== null && mon.data?.latency.p50 !== undefined ? Math.round(mon.data.latency.p50) + 'ms' : '–', '#2F5C97'],
                  ['Allowed', v.approved + pctOf(v.approved), '#1F6B52'],
                  ['Held', v.held + pctOf(v.held), '#5B6B7C'],
                ]
              : [
                  ['Gate checks', '-', '#54606C'],
                  ['Gate latency · p50', '-', '#54606C'],
                  ['Status', sel.live ? '–' : 'STAGED', '#54606C'],
                ]
            ).map(([k, val, c]) => (
              <div key={k} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:8px 0;border-top:1px solid rgba(34,39,46,.08);')}>
                <span style={sx('font-size:16px;color:#54606C;')}>{k}</span>
                <span style={sx('flex:1;')} />
                <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;color:' + c + ';')}>{val}</span>
              </div>
            ))}
            <div style={sx('font-size:14px;color:#54606C;line-height:1.4;padding-top:4px;margin-top:3px;border-top:1px solid rgba(34,39,46,.11);min-width:0;text-wrap:pretty;')}>{agingLine}</div>
          </div>
        )}
      </div>

      {ask && sel && (
        <ConfirmDialog
          title={ask === 'rotate' ? 'Rotate the key for ' + sel.name + '?' : ask === 'pause' ? 'Pause ' + sel.name + '?' : 'Promote ' + sel.name + ' to live?'}
          body={
            ask === 'rotate'
              ? 'A new key is issued and the old one fails on its next call. The new key is shown once.'
              : ask === 'pause'
                ? 'The system goes back to staged: its gate stays configured but is no longer presented as enforcing.'
                : 'The system becomes a live, enforcing fleet: every action it routes is screened before delivery and sealed to the Truth Ledger.'
          }
          confirmLabel={ask === 'rotate' ? 'Rotate key' : ask === 'pause' ? 'Pause' : 'Promote'}
          danger={ask !== 'promote'}
          onConfirm={async () => {
            setBusy(true);
            setErr(null);
            try {
              if (ask === 'rotate') {
                const r = await tenantKeyRotate(client, { params: { id: sel.id }, body: { confirm: true } });
                setNewKey({ id: sel.id, key: r.apiKey });
              } else if (ask === 'pause') {
                await governedTenantPause(client, { params: { id: sel.id }, body: {} });
              } else {
                await governedTenantPromote(client, { params: { id: sel.id }, body: {} });
              }
              tenants.reload();
            } finally {
              setBusy(false);
            }
          }}
          onClose={() => setAsk(null)}
        />
      )}
      {modal && (
        <div style={sx('position:fixed;inset:0;z-index:60;background:rgba(34,39,46,.42);display:flex;align-items:center;justify-content:center;')} onMouseDown={(e) => e.target === e.currentTarget && setModal(false)}>
          <div role="dialog" aria-modal="true" aria-label="Add System" style={sx('width:440px;max-width:92vw;background:#FCFAF4;border-radius:13px;box-shadow:0 22px 54px rgba(34,39,46,.28);overflow:hidden;')}>
            <div style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:6px;padding:16px 20px;border-bottom:1px solid rgba(34,39,46,.11);')}>
              <span style={sx('font-size:19px;font-weight:700;')}>Add System</span>
              <span style={sx('flex:1;')} />
              <Act label="Close" onClick={() => setModal(false)} css="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:7px;border:1px solid rgba(34,39,46,.16);color:#54606C;text-decoration:none;font-size:16px;white-space:nowrap;">
                ×
              </Act>
            </div>
            <div style={sx('padding:18px 20px;')}>
              <div style={sx('font-size:16px;color:#54606C;line-height:1.6;margin-bottom:15px;')}>Create a new governed system. It will start in staged mode until promoted to live.</div>
              <div style={sx(LABEL + 'margin-bottom:7px;')}>System name</div>
              <input
                type="text"
                value={name}
                aria-label="System name"
                onChange={(e) => {
                  setName(e.target.value);
                  setErr(null);
                }}
                placeholder="e.g. Atlas · Oakwood"
                style={sx('width:100%;height:40px;padding:0 12px;border:1px solid rgba(34,39,46,.16);border-radius:8px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;')}
              />
              {err && <div style={sx('font-size:16px;color:#B3261E;margin-top:8px;')}>{err}</div>}
            </div>
            <div style={sx('display:flex;gap:11px;padding:0 20px 20px;')}>
              <Act onClick={() => setModal(false)} css="flex:1;display:inline-flex;align-items:center;justify-content:center;height:42px;border-top:1px solid rgba(34,39,46,.14);color:#3C454F;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;">
                Cancel
              </Act>
              <Act
                onClick={create}
                disabled={busy || !!roReason}
                css={'flex:1;display:inline-flex;align-items:center;justify-content:center;height:42px;border-radius:9px;font-size:16px;font-weight:600;text-decoration:none;' + (name.trim() ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.2);')}
              >
                {busy ? 'Creating…' : 'Create System'}
              </Act>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
